import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { auth, functions } from './firebase';
import { httpsCallable } from 'firebase/functions';
import { usePostHog } from 'posthog-js/react';
import './CouponPage.css';

const CouponPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const posthog = usePostHog();
  const [couponCode, setCouponCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [messageType, setMessageType] = useState(null);
  const [redeemedCoupons, setRedeemedCoupons] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(true);
  const [autoSubmit, setAutoSubmit] = useState(false);

  useEffect(() => {
    // Check for coupon code in URL parameters
    const codeFromUrl = searchParams.get('code');
    const autoSubmitFlag = searchParams.get('auto') === 'true';
    
    if (codeFromUrl) {
      setCouponCode(codeFromUrl.toUpperCase());
      setAutoSubmit(autoSubmitFlag);
    }
  }, [searchParams]);

  useEffect(() => {
    // Check if user is authenticated
    const unsubscribe = auth.onAuthStateChanged(user => {
      if (!user) {
        navigate('/');
      } else {
        loadRedeemedCoupons();
        // Auto-submit if flag is set and code exists
        if (autoSubmit && couponCode && !loading && !message) {
          handleSubmit(new Event('submit'));
        }
      }
    });

    return () => unsubscribe();
  }, [navigate, autoSubmit, couponCode]);

  const loadRedeemedCoupons = async () => {
    try {
      setLoadingHistory(true);
      const getRedeemedCoupons = httpsCallable(functions, 'getRedeemedCoupons');
      const result = await getRedeemedCoupons();
      
      if (result.data.success) {
        setRedeemedCoupons(result.data.coupons || []);
      }
    } catch (error) {
      console.error('Error loading redeemed coupons:', error);
    } finally {
      setLoadingHistory(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!couponCode.trim()) {
      setMessage(t('couponPage.pleaseEnterCode'));
      setMessageType('error');
      return;
    }

    setLoading(true);
    setMessage(null);
    setMessageType(null);

    // Track coupon redemption attempt
    posthog?.capture('coupon_redemption_attempted', {
      coupon_code: couponCode,
      source: searchParams.get('code') ? 'url_parameter' : 'manual_entry',
      auto_submit: autoSubmit
    });

    try {
      const redeemCoupon = httpsCallable(functions, 'redeemCoupon');
      const result = await redeemCoupon({ code: couponCode });
      
      if (result.data.success) {
        // Track successful redemption
        posthog?.capture('coupon_redeemed', {
          coupon_code: couponCode,
          coupon_name: result.data.couponName,
          credits_awarded: result.data.credits,
          source: searchParams.get('code') ? 'url_parameter' : 'manual_entry',
          auto_submit: autoSubmit
        });

        setMessage(result.data.message || `Successfully redeemed ${result.data.credits} credits!`);
        setMessageType('success');
        setCouponCode('');
        // Reload redeemed coupons
        loadRedeemedCoupons();
      }
    } catch (error) {
      console.error('Error redeeming coupon:', error);
      let errorMessage = t('couponPage.failedToRedeem');
      
      if (error.code === 'functions/not-found') {
        errorMessage = t('couponPage.invalidCouponCode');
      } else if (error.code === 'functions/already-exists') {
        errorMessage = t('couponPage.alreadyRedeemed');
      } else if (error.code === 'functions/failed-precondition') {
        errorMessage = error.message || t('couponPage.couponExpired');
      } else if (error.code === 'functions/resource-exhausted') {
        errorMessage = t('couponPage.redemptionLimitReached');
      } else if (error.code === 'functions/permission-denied') {
        errorMessage = t('couponPage.couponNotAvailable');
      }
      
      // Track failed redemption
      posthog?.capture('coupon_redemption_failed', {
        coupon_code: couponCode,
        error_code: error.code,
        error_message: errorMessage,
        source: searchParams.get('code') ? 'url_parameter' : 'manual_entry',
        auto_submit: autoSubmit
      });
      
      setMessage(errorMessage);
      setMessageType('error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="coupon-page">
      <div className="coupon-container">
        <h1 className="coupon-title">{t('couponPage.title')}</h1>
        
        <div className="coupon-form-container">
          <p className="coupon-description">
            {searchParams.get('code') ? 
              t('couponPage.descriptionPreFilled') :
              t('couponPage.description')}
          </p>
          
          <form onSubmit={handleSubmit} className="coupon-form">
            <div className="input-group">
              <input
                type="text"
                value={couponCode}
                onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                placeholder={t('couponPage.inputPlaceholder')}
                className="coupon-input"
                disabled={loading}
                maxLength={20}
              />
              <button 
                type="submit" 
                className="redeem-button"
                disabled={loading || !couponCode.trim()}
              >
                {loading ? t('couponPage.redeeming') : t('couponPage.redeemButton')}
              </button>
            </div>
          </form>

          {message && (
            <div className={`message ${messageType}`}>
              {messageType === 'success' && '✅ '}
              {messageType === 'error' && '❌ '}
              {message}
            </div>
          )}
        </div>

        {redeemedCoupons.length > 0 && (
          <div className="redeemed-history">
            <h2>{t('couponPage.previouslyRedeemed')}</h2>
            {loadingHistory ? (
              <p>{t('couponPage.loading')}</p>
            ) : (
              <div className="redeemed-list">
                {redeemedCoupons.map((coupon, index) => (
                  <div key={index} className="redeemed-item">
                    <div className="redeemed-code">{coupon.couponName || coupon.code}</div>
                    <div className="redeemed-credits">{t('couponPage.creditsAdded', { count: coupon.credits })}</div>
                    <div className="redeemed-date">
                      {(() => {
                        if (!coupon.redeemedAt) return '';
                        
                        let date;
                        // Handle Firestore Timestamp
                        if (coupon.redeemedAt.toDate) {
                          date = coupon.redeemedAt.toDate();
                        } 
                        // Handle seconds/nanoseconds object
                        else if (coupon.redeemedAt.seconds) {
                          date = new Date(coupon.redeemedAt.seconds * 1000);
                        }
                        // Handle regular date string or number
                        else {
                          date = new Date(coupon.redeemedAt);
                        }
                        
                        // Check if date is valid
                        if (isNaN(date.getTime())) {
                          return t('couponPage.recently');
                        }
                        
                        return date.toLocaleDateString();
                      })()}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default CouponPage;